import { Directive, signal } from '@angular/core';

interface WnlTrackedControl {
  key: string;
  isWnl: () => boolean;
  isPristine: () => boolean;
  applyWnl: () => void;
  clearWnl: () => void;
}

@Directive({
  selector: '[wnlGroup]',
  exportAs: 'wnlGroup',
  standalone: true,
})
export class WnlGroupDirective {

  private controls = new Map<string, WnlTrackedControl>();
  private revision = signal(0); // value is irrelevant, only used to trigger updates (see bump())

  // Temporary explicit states set by direct master checkbox interaction.
  private forcedAllWnl: boolean | null = null;
  private suppressIndeterminate = false;



  // add control to list and return function to remove it from the list
  registerControl(control: WnlTrackedControl): () => void {
    this.controls.set(control.key, control);

    // update WNL checkbox state
    this.bump();

    // create deleter function for the control to call when destroyed
    const deleteControlFn = () => {
      this.controls.delete(control.key);
      this.bump();
    }

    return deleteControlFn;
  }

  // notify the group that a control's state has changed and it should recalculate everything
  notifyStateChanged() {
    // clear state forcing for WNL checkbox
    this.forcedAllWnl = null;
    this.suppressIndeterminate = false;

    // update checkbox state
    this.bump();
  }

  // set all controls to WNL or clear WNL based on the value of checked (called when master checkbox is interacted with)
  setAllWnl(checked: boolean) {
    // force state of WNL checkbox
    this.forcedAllWnl = checked;
    this.suppressIndeterminate = !checked;

    // update all fields
    this.controls.forEach((control) => {
      if (checked) {
        control.applyWnl();
      } else {
        control.clearWnl();
      }
    });

    // update checkbox state
    this.bump();
  }



  get allWnl(): boolean {
    this.revision(); // update when bump() is called

    // forced into some state by master checkbox interaction
    const stateForced = this.forcedAllWnl !== null;
    if (stateForced) {
      return this.forcedAllWnl!;
    }

    // if the form is untouched (pristine), allWNL will likely be true but we want the checkbox to be unchecked.
    if (this.isPristine) {
      return false;
    }

    const hasControls = this.controls.size > 0;
    const allControlsWnl = hasControls && [...this.controls.values()].every((control) => control.isWnl());
    return allControlsWnl;
  }


  // determines indeterminacy of master checkbox.
  get someWnl(): boolean {
    this.revision(); // update when bump() is called

    // forced into checked or unchecked state, not indeterminate
    if (this.forcedAllWnl !== null) {
      return false;
    }

    // if directly unchecked or not touched, not indeterminate
    const directlyUnchecked = this.suppressIndeterminate;
    const untouched = this.isPristine;
    if (directlyUnchecked || untouched) {
      return false;
    }

    const hasControls = this.controls.size > 0;
    const notAllWnl = hasControls && !this.allWnl;
    return notAllWnl;
  }


  // if no fields have been interacted with yet
  get isPristine(): boolean {
    this.revision(); // update when bump() is called
    return [...this.controls.values()].every((control) => control.isPristine());
  }


  // updates the above 3 getter functions (all just used to determine visual state of WNL checkbox)
  private bump() {
    this.revision.update((value) => value + 1);
  }
}
